import { useEffect, useRef, useState } from 'react';
import { WizardFormValues } from 'src/api/types/wizard';
import useVariables, { getIsStandardVariablesComplete } from 'src/hooks/useVariables';
import _ from 'lodash';
import { initialValues } from './WizzardWorkspaceModal/util';
import { useAuth } from 'src/state/auth';
import { WizardIntent } from 'src/redux/reducers/app/types';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import { useLocation } from 'react-router-dom';
import useWizardReminder from './useWizardReminder';
import { useAssessmentsListQuery, useWizardQuery } from 'src/graphql/_generated';
import { convertQuestionsToWizardFormValues } from './util';
import { useStateSelector } from 'src/redux';

type UseWzardInit = () => {
  isLoading: boolean;
  wizardValues: WizardFormValues | null;
  isStdVarComplete: boolean;
};

const useWzardInit: UseWzardInit = () => {
  const [wizardValues, setWizardValues] = useState<WizardFormValues | null>(null);
  const [isStdVarComplete, setIsStdVarComplete] = useState(false);
  const { profile, isTscsRequiredToAccept, features } = useAuth();
  const { isLoading: isVariablesLoading, standardVariableValues } = useVariables({});
  const { setWizardWorkspace } = useActionsApp();
  const location = useLocation();
  const startCheckDone = useRef(false);
  const { isSnoozed } = useWizardReminder();
  const wizardWorkspace = useStateSelector(({ app }) => app.wizardWorkspace);

  const autoPopupDisabled =
    !!window.localStorage.getItem('wiz_auto_disabled') || isSnoozed || features['app.wizard.popup(invert)'];
  const skipQueries = !wizardWorkspace && autoPopupDisabled;

  const { data: rawWizardData, loading: isLoadingWizard } = useWizardQuery({ skip: skipQueries });

  const { data: { assessments = [] } = {}, loading: isLoadingAssessments } = useAssessmentsListQuery({
    skip: skipQueries || !profile?.activeWorkspace?.id,
  });

  const isLoading = isVariablesLoading || isLoadingAssessments || isLoadingWizard;

  useEffect(() => {
    if (!isLoading && standardVariableValues) {
      const wizardData = convertQuestionsToWizardFormValues(rawWizardData?.profile?.activeWorkspace?.questions ?? null);
      const data = _.cloneDeep(wizardData || initialValues);

      _.set(data, 'organisation.annualRevenue', standardVariableValues.annualRevenue);
      _.set(data, 'organisation.numberOfStaff', standardVariableValues.numberOfStaff);
      _.set(data, 'organisation.annualWageBill', standardVariableValues.annualWageBill);
      _.set(data, 'organisation.avgWorkingDays', standardVariableValues.avgWorkingDays);
      _.set(data, 'organisation.annualCyberSpend', standardVariableValues.annualCyberSpend);
      _.set(data, 'organisation.avgStaffHardwareCost', standardVariableValues.avgStaffHardwareCost);

      _.set(data, 'customers.numberOfCustomers', standardVariableValues.numberOfCustomers);
      _.set(data, 'customers.avgContractValue', standardVariableValues.avgContractValue);
      _.set(data, 'customers.numberOfDataRecords', standardVariableValues.numberOfDataRecords);

      _.set(data, 'softwareDev.releaseCycleCost', standardVariableValues.releaseCycleCost);
      _.set(data, 'softwareDev.releaseCycleLength', standardVariableValues.releaseCycleLength);
      _.set(data, 'softwareDev.researchAndDevelopmentBudget', standardVariableValues.researchAndDevelopmentBudget);

      _.set(data, 'supplyChain.avgCostToProcureSupplier', standardVariableValues.avgCostToProcureSupplier);
      _.set(data, 'supplyChain.avgInvoiceValue', standardVariableValues.avgInvoiceValue);
      _.set(data, 'supplyChain.largestRegularInvoiceValue', standardVariableValues.largestRegularInvoiceValue);

      _.set(data, 'cyberInsurance.cyberInsuranceLimit', standardVariableValues.cyberInsuranceLimit);
      _.set(data, 'cyberInsurance.cyberInsuranceExcess', standardVariableValues.cyberInsuranceExcess);
      _.set(data, 'cyberInsurance.cyberInsurancePremium', standardVariableValues.cyberInsurancePremium);

      setWizardValues(data);

      const isComplete = getIsStandardVariablesComplete(standardVariableValues, {
        developsSoftware: data.softwareDev.developsSoftware,
        cyberInsurance: data.cyberInsurance.cyberInsurance,
      });

      setIsStdVarComplete(isComplete);
    }
  }, [rawWizardData, standardVariableValues, isLoading]);

  useEffect(() => {
    if (window.localStorage.getItem('wiz_auto_disabled') || isSnoozed || features['app.wizard.popup(invert)']) return;
    const isPathAllowed = ![
      '/home',
      '/organisation',
      `/organisation/${profile?.activeOrganisation?.id}`,
      '/get-started',
    ].includes(location.pathname);

    if (
      !isLoading &&
      standardVariableValues &&
      !isTscsRequiredToAccept &&
      isPathAllowed &&
      !!wizardValues &&
      !startCheckDone.current
    ) {
      const createAssessment = !assessments?.length;

      if (!isStdVarComplete) {
        setWizardWorkspace({
          intent: {
            type: WizardIntent.workspace,
            params: {
              createAssessment,
            },
          },
        });
      }

      startCheckDone.current = true;
    }
  }, [
    isLoading,
    isLoadingAssessments,
    isTscsRequiredToAccept,
    assessments,
    location.pathname,
    profile?.activeOrganisation?.id,
    isStdVarComplete,
    wizardValues,
    isSnoozed,
    features,
  ]);

  return { isLoading, wizardValues, isStdVarComplete };
};

export default useWzardInit;
